import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
  initialValuesPartnershipDetails,
  validationSchemaPartnership,
} from '@/modules/entity/components/partnership/PartnershipDetails.tsx';
import { countriesGroup1, countryOptions } from '@/modules/entity/static/countries.ts';
import { UsaPartnershipTypesOptions } from '@/modules/entity/static/usa_partnership_types.ts';
import { PartnershipEntityDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Button, Select, TextInputField } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { PlusBlueIcon, TrashIcon } from 'ui/icons';
import { UpdateEntityRequest } from 'api';

export const PartnershipDetailsHolder = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const getInitialValues = (): PartnershipEntityDetails => {
    const entityDetails = dataHolder?.data?.new_data?.entity_data?.partnership_entity_details;
    return {
      ...initialValuesPartnershipDetails,
      country:
        dataHolder?.data?.new_data?.entity_data?.country ||
        initialValuesPartnershipDetails?.country,
      ...entityDetails,
      stakeholders:
        (entityDetails?.stakeholders || [])?.map(item => ({
          ...item,
          type: item.company_name ? 'company' : 'individual',
          index: item.index || uuidv4(),
        })) || initialValuesPartnershipDetails?.stakeholders,
    };
  };

  const formik = useFormik<PartnershipEntityDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaPartnership,
    initialValues: getInitialValues(),
    onSubmit: async ({ company_name, ...details }) => {
      setDirty(false);
      if (formik.dirty) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            entity_name: company_name,
            partnership_entity_details: {
              ...dataHolder?.data?.new_data?.entity_data?.partnership_entity_details,
              ...details,
              company_name,
            },
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const changeCountry = (value: string) => {
    formik.setFieldValue('country', value);
    formik.setFieldError('country', '');

    formik.setFieldValue('entity_number', '');
    formik.setFieldError('entity_number', '');
    formik.setFieldValue('nzbn', '');
    formik.setFieldError('nzbn', '');
    formik.setFieldValue('abn', '');
    formik.setFieldError('abn', '');
    formik.setFieldValue('registered_office', '');
    formik.setFieldError('registered_office', '');
    formik.setFieldValue('partnership_type', '');
    formik.setFieldError('partnership_type', '');
  };
  const handleDeleteStakeholder = (index: string) => {
    formik.setFieldValue('stakeholders', [
      ...(formik.values.stakeholders || []).filter(item => item.index !== index),
    ]);
  };

  const handleAddStakeholder = () => {
    formik.setFieldValue('stakeholders', [
      ...(formik.values.stakeholders || []),
      { type: 'individual', first_name: '', last_name: '', index: uuidv4() },
    ]);
    formik.setFieldError('stakeholders', '');
  };

  const handleChangeSelectPartnership = (value: string | null) => {
    formik.setFieldValue(`partnership_type`, value);
    formik.setFieldError('partnership_type', '');
  };

  useEffect(() => {
    console.log('errors', formik.errors);
  }, [formik.errors]);

  return (
    <OnboardingHolderWrapper
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading}
      title="Partnership details"
    >
      <>
        <Select
          isClearable
          label="Country of Formation, Incorporation or Registration"
          options={countryOptions}
          value={formik.values.country}
          onChange={value => changeCountry(value || '')}
          error={formik.errors?.country}
        />
        <TextInputField label="Partnership name" name="company_name" />
        {formik.values.country === 'AUS' && <TextInputField label="abn" name="abn" />}
        {(formik.values.country === 'USA' ||
          countriesGroup1.includes(formik.values.country ?? '')) && (
          <TextInputField label="entity number" name="entity_number" />
        )}
        {formik.values.country === 'NZL' && <TextInputField label="NZBN" name="nzbn" />}
        {formik.values.country === 'USA' && (
          <TextInputField
            label="Registered Office Address"
            name="registered_office"
            placeholder="111/11 Name Street, North Sydney NSW 1111"
          />
        )}
        {formik.values.country === 'USA' && (
          <Select
            name="partnership_type"
            label="Partnership Type"
            options={UsaPartnershipTypesOptions}
            value={formik.values.partnership_type}
            onChange={handleChangeSelectPartnership}
            error={formik.errors.partnership_type}
          />
        )}
        <TextInputField label="Principal Place Of Business" name="principal_place_of_business" />

        <div className="w-full flex flex-col space-y-6">
          <div className="flex justify-between items-center w-full">
            <span className="text-[16px] leading-6 font-[500] font-[Inter]">
              Tell us about your stakeholders
            </span>
            <Button variant="plain" className="flex w-auto gap-1" onClick={handleAddStakeholder}>
              <img src={PlusBlueIcon} alt="Loading" />
              <span className="text-[14px] leading-[22px]">Add new stakeholder</span>
            </Button>
          </div>
          {(formik.values.stakeholders?.length ?? 0) > 0 && (
            <div className="w-full flex flex-col gap-6">
              {formik.values.stakeholders?.map((item, index) => {
                return (
                  <div key={'stakeholders_' + index} className="w-full flex flex-col gap-4">
                    <div className="w-full flex justify-between items-center">
                      <span className="font-[500] font-[Inter] text-sm leading-[22px]">
                        Stakeholder #{index + 1}
                      </span>
                      <div className="flex gap-4 items-center select-none">
                        <span className="flex gap-4 items-center"></span>

                        <img
                          onClick={() => {
                            handleDeleteStakeholder(item.index);
                          }}
                          className="cursor-pointer hover:opacity-80"
                          src={TrashIcon}
                          alt="Loading..."
                        />
                      </div>
                    </div>

                    <div className="w-full flex sm:flex-row flex-col sm:items-center items-start justify-between sm:gap-4 gap-2">
                      <TextInputField
                        label="first name"
                        name={`stakeholders[${index}].first_name`}
                      />
                      <TextInputField label="last name" name={`stakeholders[${index}].last_name`} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {typeof formik.errors?.stakeholders === 'string' && formik.errors?.stakeholders && (
            <span className="text-sm text-red-500">{formik.errors?.stakeholders}</span>
          )}
        </div>
      </>
    </OnboardingHolderWrapper>
  );
};
